import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

export interface MetricInfoDefinition {
  definition: string;
  logic?: string[];
  formula?: string;
  sources: Array<{
    system: string;
    table: string;
    columns: string[];
  }>;
  notes?: string[];
}

interface MetricInfoPopoverProps {
  title: string;
  info: MetricInfoDefinition;
  className?: string;
}

const MetricInfoPopover: React.FC<MetricInfoPopoverProps> = ({ title, info, className = '' }) => {
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const popoverId = useId();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // Position popover relative to viewport using fixed coordinates
  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const popoverWidth = Math.min(340, viewportWidth - 24);
      const margin = 12;

      let targetLeft = rect.left + rect.width / 2 - popoverWidth / 2;

      if (targetLeft < margin) {
        targetLeft = margin;
      } else if (targetLeft + popoverWidth > viewportWidth - margin) {
        targetLeft = viewportWidth - margin - popoverWidth;
      }

      setPopoverStyle({
        position: 'fixed',
        top: `${rect.bottom + 6}px`,
        left: `${targetLeft}px`,
        width: `${popoverWidth}px`,
        zIndex: 99999,
      });
    }
  }, [open]);

  // Click outside and escape handling
  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!wrapperRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  return (
    <div 
      ref={wrapperRef} 
      className={`relative inline-flex ${className}`}
      onMouseEnter={() => {
        if (window.matchMedia('(hover: hover)').matches) {
          setOpen(true);
        }
      }}
      onMouseLeave={() => {
        if (window.matchMedia('(hover: hover)').matches) {
          setOpen(false);
        }
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-label={`View details for ${title}`}
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-slate-500 hover:text-indigo-400 transition-colors focus:outline-none dark:text-slate-400 dark:hover:text-indigo-400"
      >
        <Info className="h-4 w-4" />
      </button>

      {open &&
        createPortal(
          <div
            ref={popoverRef}
            id={popoverId}
            role="dialog"
            aria-label={`${title} details`}
            style={popoverStyle}
            className="bg-[#0f172a] border border-slate-700 p-4 rounded-xl shadow-2xl text-xs text-left"
          >
            {/* Header title */}
            <div className="mb-2 pb-2 border-b border-slate-800 flex justify-between items-center">
              <span className="font-semibold text-white text-[13px]">{title}</span>
            </div>

            <div className="space-y-3">
              {/* Definition */}
              {info.definition && (
                <div>
                  <strong className="text-slate-300 block mb-1">Definition:</strong>
                  <div className="text-slate-400 leading-relaxed whitespace-pre-line">{info.definition}</div>
                </div>
              )}

              {/* Formula */}
              {info.formula ? (
                <div>
                  <strong className="text-slate-300 block mb-1">Formula:</strong>
                  <code className="bg-[#1e293b] px-2 py-1.5 rounded text-indigo-300 font-mono text-[10px] block break-words leading-normal">
                    {info.formula}
                  </code>
                </div>
              ) : (
                info.logic && info.logic.length > 0 && (
                  <div>
                    <strong className="text-slate-300 block mb-1">Logic:</strong>
                    <div className="bg-[#1e293b] px-2 py-1.5 rounded text-indigo-300 font-mono text-[10px] block break-words space-y-1 leading-normal">
                      {info.logic.map((line, idx) => (
                        <div key={idx}>{line}</div>
                      ))}
                    </div>
                  </div>
                )
              )}

              {/* Source */}
              {info.sources && info.sources.length > 0 && (
                <div className="pt-1">
                  <strong className="text-slate-300">Source:</strong>
                  <span className="text-emerald-400 font-mono text-[10px] ml-1.5">
                    {info.sources.map(s => `${s.system}.${s.table}`).join(', ')}
                  </span>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default MetricInfoPopover;
